import React, { useState, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { loadGtag } from '../util/gtag';
import { PortableText } from '@portabletext/react';
import { useStaticQuery, graphql } from 'gatsby';

// Generate a unique user identifier
function generateUserIdentifier() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showPreferenceModal, setShowPreferenceModal] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    ad_storage: true,
    analytics_storage: true,
    functionality_storage: true,
    personalization_storage: true,
    security_storage: true,
    consent: true
  });
  const [expandedSections, setExpandedSections] = useState({
    analytics_storage: false,
    consent: false
  });
  const [userIdentifier, setUserIdentifier] = useState(null);
  
  const sectionRefs = useRef({});
  const [sectionHeights, setSectionHeights] = useState({});
  
  useEffect(() => {
    // Initialize section heights
    const heights = {};
    Object.keys(expandedSections).forEach(sectionId => {
      if (sectionRefs.current[sectionId]?.content) {
        heights[sectionId] = sectionRefs.current[sectionId].content.scrollHeight + 'px';
      }
    });
    setSectionHeights(heights);
  }, []);

  const data = useStaticQuery(graphql`
    query ConsentModalTextQuery {
      sanitySettings: sanitySiteSettings(_id: { regex: "/(drafts\\\\.)?siteSettings/" }) {
        _rawConsentModalText(resolveReferences: { maxDepth: 10 })
        _rawAnalyticsCookieDescription(resolveReferences: { maxDepth: 10 })
        _rawAdvertisingCookieDescription(resolveReferences: { maxDepth: 10 })
        _rawFunctionalityCookieDescription(resolveReferences: { maxDepth: 10 })
        _rawPersonalizationCookieDescription(resolveReferences: { maxDepth: 10 })
        _rawSecurityCookieDescription(resolveReferences: { maxDepth: 10 })
        _rawConsentCookieDescription(resolveReferences: { maxDepth: 10 })
      }
    }
  `);

  useEffect(() => {
    // Get or generate user identifier
    let userId = localStorage.getItem('user-consent');
    if (!userId || userId === 'true') {
      userId = generateUserIdentifier();
      if (localStorage.getItem('user-consent') === 'true') {
        localStorage.setItem('user-consent', userId);
      }
    }
    setUserIdentifier(userId);

    const cookiesAccepted = localStorage.getItem('user-consent');
    const cookiePrefs = localStorage.getItem('cookie-preferences');

    if (cookiesAccepted && cookiesAccepted !== 'false') {
      grantGtagConsent();
      setVisible(false);
    } else if (cookiePrefs) {
      try {
        const prefs = JSON.parse(cookiePrefs);
        setCookiePreferences(prefs);
        
        if (prefs.analytics_storage || prefs.ad_storage || 
            prefs.functionality_storage || prefs.personalization_storage || 
            prefs.security_storage) {
          grantGtagConsent(prefs);
        }
        
        setVisible(false);
      } catch (e) {
        setVisible(true);
      }
    } else {
      setVisible(true);
    }
  }, []);

  const acceptAllCookies = () => {
    const newPrefs = {
      ad_storage: true,
      analytics_storage: true,
      functionality_storage: true,
      personalization_storage: true,
      security_storage: true,
      consent: true
    };
    
    localStorage.setItem('user-consent', userIdentifier);
    localStorage.setItem('cookie-preferences', JSON.stringify(newPrefs));
    setCookiePreferences(newPrefs);
    grantGtagConsent();
    setVisible(false);
    setShowPreferenceModal(false);
    
    // Log consent to Supabase
    logConsent(newPrefs, 'accept_all');
  };

  const rejectAllCookies = () => {
    const newPrefs = {
      ad_storage: false,
      analytics_storage: false,
      functionality_storage: false,
      personalization_storage: false,
      security_storage: false,
      consent: false
    };
    
    // Don't store preferences if consent is false
    localStorage.removeItem('user-consent');
    localStorage.removeItem('cookie-preferences');
    setCookiePreferences(newPrefs);
    setVisible(false);
    setShowPreferenceModal(false);
  };

  const confirmChoices = () => {
    if (cookiePreferences.consent) {
      localStorage.setItem('cookie-preferences', JSON.stringify(cookiePreferences));
      
      if (cookiePreferences.analytics_storage || cookiePreferences.ad_storage || 
          cookiePreferences.functionality_storage || cookiePreferences.personalization_storage || 
          cookiePreferences.security_storage) {
        localStorage.setItem('user-consent', userIdentifier);
        grantGtagConsent(cookiePreferences);
      } else {
        localStorage.removeItem('user-consent');
      }
      
      // Log consent to Supabase only when consent is given
      logConsent(cookiePreferences, 'custom_preferences');
    } else {
      // Don't store preferences if consent is false
      localStorage.removeItem('user-consent');
      localStorage.removeItem('cookie-preferences');
    }
    
    setVisible(false);
    setShowPreferenceModal(false);
  };

  const toggleCookieType = (type) => {
    setCookiePreferences({
      ...cookiePreferences,
      [type]: !cookiePreferences[type]
    });
  };

  const toggleSection = (section) => {
    // Ensure we have the height measurement before toggling
    if (!sectionHeights[section] && sectionRefs.current[section]?.content) {
      setSectionHeights(prev => ({
        ...prev,
        [section]: sectionRefs.current[section].content.scrollHeight + 'px'
      }));
    }
    
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  const openPreferenceModal = (e) => {
    e.preventDefault();
    setShowPreferenceModal(true);
  };

  const logConsent = async (preferences, method) => {
    if (!userIdentifier) return;
    
    const purposes = [];
    if (preferences.analytics_storage) purposes.push('analytics');
    if (preferences.ad_storage) purposes.push('advertising');
    if (preferences.functionality_storage) purposes.push('functionality');
    if (preferences.personalization_storage) purposes.push('personalization');
    if (preferences.security_storage) purposes.push('security');
    
    const consentData = {
      user_identifier: userIdentifier,
      consent_status: preferences.consent,
      consent_purposes: purposes.join(','),
      consent_method: method,
      consent_version: '1.0',
      proof_of_consent: `${method} on ${preferences.consent ? 'accept' : 'reject'} at ${new Date().toISOString()}`
    };
    
    try {
      await fetch('/api/log-consent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(consentData)
      });
    } catch (error) {
      console.error('Failed to log consent:', error);
    }
  };

  if (!visible && !showPreferenceModal) return null;

  function grantGtagConsent(preferences = cookiePreferences) {
    loadGtag();

    const consentOptions = {
      ad_storage: preferences.ad_storage ? 'granted' : 'denied',
      analytics_storage: preferences.analytics_storage ? 'granted' : 'denied',
      functionality_storage: preferences.functionality_storage ? 'granted' : 'denied',
      personalization_storage: preferences.personalization_storage ? 'granted' : 'denied',
      security_storage: preferences.security_storage ? 'granted' : 'denied'
    };

    window.gtag('consent', 'update', consentOptions);

    window.gtag('event', 'page_view', {
      page_path: window.location.pathname + window.location.search,
    });
  }

  const buttonStyles = css`
    background-color: transparent;
    color: var(--black);
    border: 3px solid var(--black);
    border-radius: 2.5rem;
    padding: 1rem 1.5rem;
    cursor: pointer;
    
    &:hover {
      background-color: var(--black);
      color: var(--white);
    }
  `;

  const toggleStyles = css`
    position: relative;
    display: inline-block;
    width: 4.4rem;
    height: 2.8rem;
    
    input {
      opacity: 0;
      width: 0;
      height: 0;
    }
    
    .slider {
      position: absolute;
      cursor: pointer;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .slider svg {
      transition: .4s;
      width: 100%;
      height: 100%;
    }
  `;

  return (
    <>
      {visible && !showPreferenceModal && (
        <div
          css={css`
            position: fixed;
            bottom: 4rem;
            right: 4rem;
            background-color: var(--yellow);
            padding: 2rem;
            max-width: 32rem;
            box-shadow: 0 0.4rem 1rem rgba(0, 0, 0, 0.1);
            transform: scale(0.8);
            transform-origin: bottom right;
            z-index: 1001;
            
            @media (max-width: 800px) {
              bottom: 3rem;
              right: 3rem;
              transform: scale(0.9);
            }

            @media (max-width: 400px) {
              bottom: 1.5rem;
              right: 1.5rem;
              left: 1.5rem;
              padding: 1.5rem;
              max-width: none;
              transform: scale(1);
            }
          `}
        >
          <div
            className="type--body-small"
            css={css`
              margin-bottom: 1.5rem;
            `}
          >
            We use cookies to analyse how our site is used. <a href="#" onClick={openPreferenceModal}>Manage</a> what cookies you share, or accept all to continue.
          </div>
          <button
            className="type--body-small"
            css={buttonStyles}
            onClick={acceptAllCookies}
          >
            Accept All Cookies
          </button>
        </div>
      )}

      {showPreferenceModal && (
        <>
          <div
            css={css`
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: rgba(0, 0, 0, 0.3);
              z-index: 1002;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 2.5rem;

              @media (max-width: 800px) {
                padding: 2rem;
              }
            `}
          >
            <div
              css={css`
                background-color: var(--white);
                width: 52rem;
                max-height: calc(100svh - 12rem);
                display: flex;
                flex-direction: column;
                box-shadow: 0 0.4rem 2rem rgba(0, 0, 0, 0.2);
                overflow: hidden;

                @media (max-width: 800px) {
                  max-height: calc(100svh - 4rem);
                  overflow: auto;
                }
              `}
            >
              <div
                css={css`
                  padding: 2.5rem;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                `}
              >
                <h2 className="type--paragraph">Privacy Preference Centre</h2>
                <button
                  css={css`
                    background: none;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.5rem;
                    
                    &:hover {
                      svg {
                        circle {
                          fill: black;
                        }
                        path {
                          fill: white;
                          stroke: white;
                        }
                      }
                    }
                  `}
                  onClick={() => setShowPreferenceModal(false)}
                >
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="14" cy="14" r="12" fill="transparent" stroke="black" strokeWidth="3"/>
                    <path d="M14 15.4352L17.5648 19L19 17.5648L15.4352 14L19 10.4352L17.5648 9L14 12.5648L10.4352 9L9 10.4352L12.5648 14L9 17.5648L10.4352 19L14 15.4352Z" fill="black" stroke="black"/>
                  </svg>
                </button>
              </div>

              <div
                css={css`
                  padding: 0 2.5rem;
                  display: flex;
                  flex-direction: column;
                  overflow: hidden;
                  flex: 1;

                  @media (max-width: 800px) {
                    overflow-y: auto;
                  }
                `}
              >
                <div className="type--body-small">
                  {data.sanitySettings?._rawConsentModalText && (
                    <PortableText value={data.sanitySettings._rawConsentModalText} />
                  )}
                </div>

                <div
                  css={css`
                    margin-top: 2rem;
                  `}
                >
                  <button
                    className="type--body-small"
                    css={buttonStyles}
                    onClick={acceptAllCookies}
                  >
                    Accept All Cookies
                  </button>
                </div>

                <div
                  css={css`
                    margin-top: 4rem;
                    margin-bottom: 1.5rem;

                    @media (max-width: 800px) {
                      margin-top: 3rem;
                    }
                  `}
                >
                  <h3 className="type--paragraph">Manage Consent Preferences</h3>
                </div>

                <div
                  css={css`
                    border-top: 3px solid var(--black);
                    border-bottom: 3px solid var(--black);
                    overflow-y: auto;
                    flex: 1;

                    @media (max-width: 800px) {
                      overflow-y: visible;
                    }
                  `}
                >
                  {[
                    {
                      id: 'analytics_storage',
                      title: 'Analytics',
                      description: data.sanitySettings?._rawAnalyticsCookieDescription || 'Helps us understand how visitors interact with our website by collecting and reporting information anonymously. This allows us to measure and improve the performance of our site.'
                    },
                    {
                      id: 'ad_storage',
                      title: 'Advertising',
                      description: data.sanitySettings?._rawAdvertisingCookieDescription || 'Used to make advertising messages more relevant to you. Performs functions like preventing the same ad from continuously reappearing, ensuring that ads are properly displayed, and in some cases selecting advertisements that are based on your interests.'
                    },
                    {
                      id: 'functionality_storage',
                      title: 'Functionality',
                      description: data.sanitySettings?._rawFunctionalityCookieDescription || 'Enables the website to provide enhanced functionality and personalization. May be set by us or by third party providers whose services we have added to our pages.'
                    },
                    {
                      id: 'personalization_storage',
                      title: 'Personalization',
                      description: data.sanitySettings?._rawPersonalizationCookieDescription || 'Allows the site to remember choices you make and provide enhanced, more personal features. May also be used to provide services you have asked for.'
                    },
                    {
                      id: 'security_storage',
                      title: 'Security',
                      description: data.sanitySettings?._rawSecurityCookieDescription || 'Helps identify and prevent security risks. May be used to store information that prevents fraud and protect your data from unauthorized parties.'
                    },
                    {
                      id: 'consent',
                      title: 'Consent',
                      description: data.sanitySettings?._rawConsentCookieDescription || 'Necessary to store your consent preferences and enable basic functionality of our website.'
                    }
                  ].map((section, index) => (
                    <div
                      key={section.id}
                      css={css`
                        border-bottom: 3px solid var(--black);

                        &:last-of-type {
                          border-bottom: none;
                        }
                      `}
                    >
                      <div
                        css={css`
                          width: 100%;
                          display: flex;
                          justify-content: space-between;
                          align-items: center;
                          padding: 1rem 0;
                        `}
                        ref={el => {
                          if (!sectionRefs.current[section.id]) {
                            sectionRefs.current[section.id] = {};
                          }
                          sectionRefs.current[section.id].header = el;
                        }}
                      >
                        <button
                          css={css`
                            display: flex;
                            align-items: center;
                            background: none;
                            border: none;
                            color: black;
                            cursor: pointer;
                            text-align: left;
                            padding: 0;

                            &:hover {
                              svg {
                                circle {
                                  fill: black;
                                }

                                path {
                                  fill: white;
                                  stroke: white;
                                }
                              }
                            }
                          `}
                          onClick={() => toggleSection(section.id)}
                        >
                          <div className="type--body-small" css={css`display: flex; align-items: center;`}>
                            <svg 
                              width="28" 
                              height="28" 
                              viewBox="0 0 28 28" 
                              fill="none" 
                              xmlns="http://www.w3.org/2000/svg"
                              css={css`
                                transform: ${expandedSections[section.id] ? 'rotate(45deg)' : 'rotate(0deg)'};
                                transition: 0.6s transform var(--curve);
                              `}
                            >
                              <circle cx="14" cy="14" r="12" fill="transparent" stroke="black" strokeWidth="3"/>
                              <path d="M12.9852 15.2753L12.9852 20.3168L15.0148 20.3168L15.0148 15.2754L20.0562 15.2754L20.0562 13.2457L15.0148 13.2457L15.0148 8.20428L12.9852 8.20428L12.9852 13.2457L7.94376 13.2457L7.94376 15.2753L12.9852 15.2753Z" fill="black" stroke="black"/>
                            </svg>
                            <span css={css`margin-left: 2rem;`}>{section.title}</span>
                          </div>
                        </button>
                        <label css={toggleStyles} checked={cookiePreferences[section.id]}>
                          <input
                            type="checkbox"
                            checked={cookiePreferences[section.id]}
                            onChange={() => toggleCookieType(section.id)}
                          />
                          <span className="slider">
                            <svg width="44" height="28" viewBox="0 0 44 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect 
                                x="1.5" 
                                y="1.5" 
                                width="41" 
                                height="25" 
                                rx="12.5" 
                                fill={cookiePreferences[section.id] ? "black" : "white"}
                                css={css`transition: fill 0.4s var(--curve);`}
                              />
                              <rect 
                                x="1.5" 
                                y="1.5" 
                                width="41" 
                                height="25" 
                                rx="12.5" 
                                stroke="black" 
                                strokeWidth="3"
                              />
                              <rect 
                                x={cookiePreferences[section.id] ? "22" : "7"} 
                                y="6" 
                                width="16" 
                                height="16" 
                                rx="8" 
                                fill={cookiePreferences[section.id] ? "white" : "black"}
                                css={css`transition: fill 0.4s var(--curve), x 0.4s var(--curve);`}
                              />
                            </svg>
                          </span>
                        </label>
                        </div>
                      
                      <div
                        css={css`
                          overflow: hidden;
                          height: ${expandedSections[section.id] ? (sectionHeights[section.id] || 'auto') : '0px'};
                          transition: height 0.6s var(--curve);
                          will-change: height;
                        `}
                      >
                        <div
                          className="type--body-small"
                          css={css`
                            padding-bottom: 1.5rem;
                          `}
                          ref={el => {
                            if (!sectionRefs.current[section.id]) {
                              sectionRefs.current[section.id] = {};
                            }
                            sectionRefs.current[section.id].content = el;
                            
                            if (el && !sectionHeights[section.id]) {
                              setSectionHeights(prev => ({
                                ...prev,
                                [section.id]: el.scrollHeight + 'px'
                              }));
                            }
                          }}
                        >
                        {Array.isArray(section.description) ? (
                          <>
                            <PortableText value={section.description} />
                          </>
                        ) : (
                          <>
                            {section.description}
                          </>
                        )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                css={css`
                  padding: 2.5rem;
                  margin-top: 2rem;
                  display: flex;
                  flex-wrap: wrap;
                  gap: 1rem 2rem;

                  @media (max-width: 800px) {
                    margin-top: 1rem;
                  }
                `}
              >
                <button
                  className="type--body-small"
                  css={buttonStyles}
                  onClick={rejectAllCookies}
                >
                  Reject All Cookies
                </button>
                <button
                  className="type--body-small"
                  css={buttonStyles}
                  onClick={confirmChoices}
                >
                  Confirm My Choices
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
